// Bag-label rule engine — ESM twin of api-server/utils/bagLabelRules.js (CJS).
// The two files are logic-mirrored by hand (Contract C3): any rule change here
// must be made in the server file too, and vice versa.
//
// label: { UniqueID, LabelName, CustomersCsv, CommoditiesCsv, TypesCsv, StylesCsv, IsActive, ImageLink }
// style: { UniqueID, Type }  (Type may be null/blank)
// commodityId: the spec's Commodities.UniqueID (null/blank when the spec has none)
// Wire selection form, everywhere: null = Default (live-follow) · 'NONE' = manually
// cleared · [ints] = pinned BagLabels.UniqueID. Stored CSV: NULL / 'NONE' / '3,7'.

const csvSet = (csv, upper = false) => new Set(
  String(csv || '')
    .split(',')
    .map((s) => (upper ? s.trim().toUpperCase() : s.trim()))
    .filter(Boolean)
);

// Active labels this customer can see: CustomersCsv 'All' or containing the id.
export function visibleLabels(labels, customerId) {
  const cid = String(customerId ?? '');
  return (labels || []).filter((l) => l && l.IsActive
    && (l.CustomersCsv === 'All' || csvSet(l.CustomersCsv).has(cid)));
}

// Commodity/Type/Style scoping. A blank style Type never matches a specific TypesCsv
// set; likewise a spec with no commodity never matches a specific CommoditiesCsv set.
// A null CommoditiesCsv (row written before the column existed) reads as 'Any'.
export function matches(label, style, commodityId) {
  const type = String(style?.Type ?? '').trim().toUpperCase();
  const commodityOk = label.CommoditiesCsv == null || label.CommoditiesCsv === 'Any'
    || csvSet(label.CommoditiesCsv).has(String(commodityId ?? '').trim());
  const typeOk = label.TypesCsv === 'Any' || csvSet(label.TypesCsv, true).has(type);
  const styleOk = label.StylesCsv === 'Any' || csvSet(label.StylesCsv).has(String(style?.UniqueID));
  return commodityOk && typeOk && styleOk;
}

// Most-specific match wins: score tuple (Customers, Commodities, Types, Styles), 1 per
// non-sentinel field, compared lexicographically (Customers first). A unique max is the
// auto default; two or more sharing the max tuple is a tie (no default).
export function autoDefault(visible, style, commodityId) {
  const candidates = (visible || []).filter((l) => matches(l, style, commodityId));
  if (candidates.length === 0) return { label: null, reason: 'none' };
  const score = (l) => [
    l.CustomersCsv !== 'All' ? 1 : 0,
    (l.CommoditiesCsv != null && l.CommoditiesCsv !== 'Any') ? 1 : 0,
    l.TypesCsv !== 'Any' ? 1 : 0,
    l.StylesCsv !== 'Any' ? 1 : 0,
  ];
  const cmp = (a, b) => (a[0] - b[0]) || (a[1] - b[1]) || (a[2] - b[2]) || (a[3] - b[3]);
  let best = [candidates[0]];
  let bestScore = score(candidates[0]);
  for (const l of candidates.slice(1)) {
    const s = score(l);
    const c = cmp(s, bestScore);
    if (c > 0) { best = [l]; bestScore = s; }
    else if (c === 0) best.push(l);
  }
  if (best.length > 1) return { label: null, reason: 'tie' };
  return { label: best[0], reason: 'auto' };
}

// Effective labels for one style. Pins are filtered to visible (drift drop) but NOT
// by matches() — off-type / off-commodity pins are allowed. Order follows the input labels array
// (canonical = GET /api/baglabels, LabelName ASC).
export function resolveForStyle(selection, labels, customerId, style, commodityId) {
  const visible = visibleLabels(labels, customerId);
  if (selection == null) {
    const auto = autoDefault(visible, style, commodityId);
    return auto.label ? [auto.label] : [];
  }
  if (selection === 'NONE') return [];
  const ids = new Set((Array.isArray(selection) ? selection : []).map(Number));
  return visible.filter((l) => ids.has(Number(l.UniqueID)));
}

// Stored CSV -> wire form. NULL/empty -> null · 'NONE' -> 'NONE' · '3,7' -> [3,7]
// (deduped). No parseable ids => fail open to Default rather than an empty pin list.
export function parseSelectionCsv(csv) {
  const raw = String(csv ?? '').trim();
  if (!raw) return null;
  if (raw === 'NONE') return 'NONE';
  const ids = [...new Set(raw.split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isInteger(n) && n > 0))];
  return ids.length ? ids : null;
}

// Wire form -> stored CSV (write normalization per Contract C2): absent/null -> null;
// 'NONE' or an array that empties after int-filtering (incl. []) -> 'NONE'.
export function selectionCsvFrom(wire) {
  if (wire == null) return null;
  if (wire === 'NONE') return 'NONE';
  if (Array.isArray(wire)) {
    const ids = [...new Set(wire.map(Number).filter((n) => Number.isInteger(n) && n > 0))];
    return ids.length ? ids.join(',') : 'NONE';
  }
  return null;
}

// UI-only helper (not mirrored server-side): names of Bag-ProductType styles whose
// selection resolves to zero labels — the save-time warn set (Decision 6, warn-don't-
// block). Empty while no labels exist at all (fresh feature / failed fetch): a warning
// about missing rules is meaningless before any rule can exist.
export function zeroBagStyleNames(styleEntries, styles, bagLabelRows, customerId, commodityId) {
  if (!Array.isArray(bagLabelRows) || bagLabelRows.length === 0) return [];
  return (styleEntries || [])
    .map((e) => (e && typeof e === 'object' ? e : { value: e, bagLabels: null }))
    .map((e) => ({ e, row: (styles || []).find((s) => Number(s.UniqueID) === Number(e.value)) }))
    .filter(({ e, row }) => row?.ProductType === 'Bag'
      && resolveForStyle(e.bagLabels ?? null, bagLabelRows, customerId, row, commodityId).length === 0)
    .map(({ row }) => String(row.Style || row.UniqueID));
}

// UI-only helper (not mirrored server-side): Bag-ProductType styles left on Default
// whose rules TIE (two or more equally specific matches). A tie resolves to no label,
// so the form blocks the save until one is picked explicitly — unlike the zero-match
// case, a tie is ambiguity the form submitter can actually resolve. Returns
// [{ id, name }]; empty while no labels exist at all (same guard as zeroBagStyleNames).
export function tiedBagStyles(styleEntries, styles, bagLabelRows, customerId, commodityId) {
  if (!Array.isArray(bagLabelRows) || bagLabelRows.length === 0) return [];
  const visible = visibleLabels(bagLabelRows, customerId);
  return (styleEntries || [])
    .map((e) => (e && typeof e === 'object' ? e : { value: e, bagLabels: null }))
    .map((e) => ({ e, row: (styles || []).find((s) => Number(s.UniqueID) === Number(e.value)) }))
    .filter(({ e, row }) => row?.ProductType === 'Bag'
      && (e.bagLabels ?? null) === null
      && autoDefault(visible, row, commodityId).reason === 'tie')
    .map(({ row }) => ({ id: Number(row.UniqueID), name: String(row.Style || row.UniqueID) }));
}

// UI-only helper (not mirrored server-side): the customer dropdown collapses duplicate
// customer rows to MIN(UniqueID) per name, and label CustomersCsv stores those dropdown
// ids — so resolve a spec's customer by NAME against the dropdown before rule checks,
// falling back to the spec's stored CustomerID (Risk R3).
export function canonicalCustomerId(customersDropdown, spec) {
  const name = String(spec?.CustomerName || '').trim();
  if (name) {
    const hit = (customersDropdown || []).find((c) => String(c.Customer || '').trim() === name);
    if (hit) return Number(hit.UniqueID);
  }
  const id = Number(spec?.CustomerID);
  return Number.isFinite(id) && id > 0 ? id : null;
}
